import { CommonModule } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, DestroyRef, effect, ElementRef, Inject, InjectionToken, Optional, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';

export interface LoginActionLink {
  labelKey?: string;
  label?: string;
  routerLink?: string | string[];
  /**
   * Policy document key, passed straight to `/media/:lang/policy/:key`.
   *
   * Deliberately a free string, not a union: the key set is per config domain,
   * not global. fac3less (0) and mnlo (6) publish `terms_of_service`/
   * `privacy_policy`/`legal_notice`; distribution (4), globalhealth (5) and
   * wolfpack-assets (7) publish `terms`/`privacy`/`imprint`. PolicyController
   * renders whatever template exists under that domain's media directory and
   * raises MissingTemplate (500) for a key it does not publish — so a union of
   * one domain's names would be wrong for the others.
   */
  policy?: string;
}

export interface LoginConfig {
  register?: boolean;
  /**
   * Where "Register" leads. Defaults to the app's `/register` page; a brand whose
   * sign-up is its acquisition funnel points it there instead (Global Health: `/r`).
   */
  registerUrl?: string;
  passwordRecovery?: boolean;
  appInstallBottomsheet?: boolean;
  actions?: LoginActionLink[];
}

export const LOGIN_CONFIG = new InjectionToken<LoginConfig>('LoginConfig');

import {
  ButtonCloseDirective,
  ButtonDirective, CardBodyComponent, CardComponent,
  CardGroupComponent, ColComponent, ContainerComponent,
  FormDirective, InputGroupComponent, InputGroupTextDirective,
  OffcanvasModule,
  RowComponent
} from '@coreui/angular-pro';
import { IconDirective } from '@coreui/icons-angular';

import { animate, style, transition, trigger } from '@angular/animations';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { AuthService } from 'auth';
import { AuthLanguageSwitcherComponent, I18nService, TranslateDirective, TranslatePipe, TranslateService } from 'i18n';
import { AppInstallComponent } from 'libs/app/src/components/app-install/app-install.component';
import { BottomSheetRef, BottomSheetService, clipboardCopy, ConsentComponent, ENVIRONMENT, Logger, LOGO, LogoComponent, Platform, SafeHtmlPipe, SpinnerComponent, UnassistedInputDirective } from 'shared';
import { AppInstallBottomSheetComponent } from '../../../components/app-install/app-install-bottom-sheet';
import { RouteService } from '../../../services/route.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';

@Component({
  selector: 'app-login-page',
  styleUrl: './login-page.component.scss',
  templateUrl: './login-page.component.html',
  imports: [
    CommonModule,       // Includes NgIf, NgFor, etc. + NgStyle
    FormsModule,        // For [(ngModel)]
    ContainerComponent,
    ButtonCloseDirective,
    ConsentComponent,
    RowComponent,
    ColComponent,
    CardGroupComponent,
    CardComponent,
    CardBodyComponent,
    FormDirective,
    InputGroupComponent,
    InputGroupTextDirective,
    ButtonDirective,
    IconDirective,
    SpinnerComponent,
    UnassistedInputDirective,
    TranslateDirective,
    TranslatePipe,
    AuthLanguageSwitcherComponent,
    AppInstallComponent,
    LogoComponent,
    RouterLink,
    OffcanvasModule,
    SafeHtmlPipe
  ],
  animations: [
    /* 
      Does not work likely because:
      1. Both <div> elements share the same trigger, Angular’s animation engine struggles to correctly distinguish
         which element is :enter and which is :leave within the group block
      2. Usage of <ng-container> can confuse the query(':enter') and query(':leave') selectors,
         as they expect to find direct child elements entering or leaving the DOM. 
         Since the <ng-container> itself isn’t animated, and the template content is complex (with nested <div> and other elements), 
         Angular may fail to identify the correct elements to animate.
    */
    // trigger('panelTransition', [
    //   transition('* <=> *', [
    //     group([
    //       query(':enter', [
    //         style({ opacity: 0}),
    //         animate('4000ms ease-in-out', style({ opacity: 1,})),
    //       ],  { optional: true }),
    //       query(':leave', [
    //         animate('4000ms ease-in-out', style({ opacity: 0 })),
            
    //       ], { optional: true })
    //     ])
    //   ])
    // ]),
    trigger('panelTransition', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('400ms ease-out', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('400ms ease-in', style({ opacity: 0 }))
      ])
    ]),
    trigger('alertTransition', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(0)' }),
        animate('500ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('500ms ease-in', style({ opacity: 0, transform: 'translateY(0)' }))
      ])
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginPageComponent {

  email: string = '';
  password: string = '';
  token = signal<string | null>(null);
  error = signal<string | null>(null);

  offcanvas = viewChild('offcanvas', { read: ElementRef });

  content = signal<string>('');
  loading = signal<boolean>(false);
  sidePanelVisible = signal<boolean | undefined>(undefined);

  footer = toSignal(this.translateService.translateAsync('login.footer'), {initialValue: null});

  protected bottomSheetRef: BottomSheetRef | null = null;
  protected language: string = 'en';
  protected tokenRegex = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
  protected logger = new Logger('LoginPageComponent');

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private routeService: RouteService,
    private authService: AuthService,
    private bottomSheetService: BottomSheetService,
    private httpClient: HttpClient,
    private i18nService: I18nService,
    public platform: Platform,
    private inAppBrowser: InAppBrowser,
    private translateService: TranslateService,
    private destroyRef: DestroyRef,
    @Inject(ENVIRONMENT) protected environment: any,
    @Inject(LOGO) public logo: string[],
    @Inject(LOGIN_CONFIG) @Optional() public config?: LoginConfig
  ) {
    this.logger.debug('ctor');
    this.route.params
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(params => {
          this.language = params['lang'] || localStorage.getItem('language') || 'en';
        });
    afterNextRender(() => {
      // when clipboard paste api is called ios webview blocks rendering and displays paste button on top left corner (at least on ipad).
      // to avoid presenting an empty page to the user ensure that the content is fully rendered before performing clipboard data retrieval
      setTimeout(() => this.handleToken(), 100);
    });

    // effect(() => {
    //   const sidePanelVisible = this.sidePanelVisible();
    //   if (sidePanelVisible) {
    //     const offcanvas = this.offcanvas()?.nativeElement;
    //     if (offcanvas) {
    //       offcanvas.scrollTop = 0;
    //     }
    //     const baseUrl = (url: string) => {
    //       try {
    //         const urlObj = new URL(url);
    //         return `${urlObj.protocol}/${urlObj.host}`;
    //       } catch (e) {
    //         console.error('Invalid URL:', url);
    //         return null;
    //       }
    //     }
    //     const url = baseUrl(this.environment.serverUrl)
    //     if (url) {
    //       this.httpClient
    //           .get(`/media/${this.i18nService.getLanguage()}/policy/privacy_policy`, {
    //             responseType: 'text' as 'text',
    //             params: { embed: 'true' }
    //           })
    //           .pipe(takeUntilDestroyed(this.destroyRef))
    //           .subscribe(tos => {
    //             this.logger.debug(tos);
    //             this.content.set(tos || '');
    //           });
    //     }
    //   }
    // });
  }

  ngOnInit(): void {
    // new Promise<string | null>(async(resolve) => {
    //   let token = this.route.snapshot.params['token'] || history.state?.['token'];
    //   if (!token && this.platform.is('hybrid')) {
    //       token = await this.getTokenFromClipboard();
    //       if (token) {
    //         // Clear clipboard after reading token
    //         try {
    //           await navigator.clipboard.writeText('');
    //         } catch (error) {
    //           this.logger.warn('Failed to clear clipboard', error);
    //         }
    //       }
    //   }
    //   resolve(token);
    // })
    // .then(token=> {
    //   this.token.set(token);
    //   this.logger.debug('Login token:', this.token());
    //   if (token && (this.platform.is('desktop') || this.platform.is('hybrid'))) {
    //     this.loginWithJwt(token);
    //   } else if (!token) {
    //     const logout = history.state?.['logout'];
    //     if (!logout && !this.authService.isLoggedIn() && !this.platform.is('hybrid')) {
    //       if (this.config.appInstallBottomsheet!=false) {
    //         setTimeout(() => this.displayAppInstallBottomSheet(), 1000);
    //       }
          
    //     }
    //   }
    // });
  }

  loginWithJwt(token: string) {
    return this.authService.loginWithJwt(token)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {}/*this.router.navigateByUrl(this.routeService.getHomeUrl())*/,
        error: (error) => {
          this.logger.error(JSON.stringify(error));
          this.error.set("invalidToken");
          if (this.platform.is('desktop') && 
              this.config?.appInstallBottomsheet!=false) {
            this.displayAppInstallBottomSheet();
          }
        }
      });
  }

  login() {
    this.authService
      .login({ username: this.email, password: this.password })
      .subscribe({
        next: (response) => {
          this.bottomSheetRef?.dispose();
          // login <-> logout navigation handled ONLY by RouteService!
          // this.router.navigateByUrl(this.routeService.getHomeUrl())
        },
        error: (error) => {
          this.logger.error('Login failure', error);
          this.error.set('invalidCredentials');
        }
      });
  }

  onTapAppInstallLink() {
    const token = this.token();
    if (token && this.platform.is('mobile')) {
      // make token available to the eventually installed and started app
      clipboardCopy(token).catch((error: any) => this.logger.error("Failed clipboard token copy"));
    }
  }

  protected async handleToken(): Promise<void> {
    let token: string | null = this.route.snapshot.params['token'] ?? history.state?.['token'] ?? null;
    if (!token && this.platform.is('hybrid')) {
      token = await this.getTokenFromClipboard();
      if (token) {
        await navigator.clipboard.writeText('').catch(err =>
          this.logger.warn('Failed to clear clipboard', err)
        );
      }
    }
  
    this.token.set(token);
    this.logger.debug('Login token:', this.token());
  
    if (token && (this.platform.is('desktop') || this.platform.is('hybrid'))) {
      this.loginWithJwt(token);
      return;
    }
  
    // No token - possibly show install prompt
    const shouldLogout = history.state?.['logout'] === true;
    const shouldShowInstallPrompt =
      !shouldLogout &&
      !this.authService.isLoggedIn() &&
      !this.platform.is('hybrid') &&
      this.config?.appInstallBottomsheet !== false;
  
    if (shouldShowInstallPrompt) {
      setTimeout(() => this.displayAppInstallBottomSheet(), 1000);
    }
  }
  
  protected async getTokenFromClipboard(): Promise<string | null> {
    try {
      const token = await navigator.clipboard.readText();
      if (token) {
        if (token.match(this.tokenRegex)) {
          return token;
        } else {
          this.logger.warn(`Invalid token in clipboard: ${token}`);
        }
      }
      return null;
    } catch (error) {
      this.logger.error('Failed to read from clipboard', error);
      return null;
    }
  }

  protected displayAppInstallBottomSheet() {
    this.bottomSheetRef = this.bottomSheetService.openBottomSheet(
      AppInstallBottomSheetComponent, {
      data: {},
      disableBackdrop: true,
      disableClose: false,
      panelClass: 'app-install-bottom-sheet',
      animationDuration: 400
    })
    this.bottomSheetRef
        .afterDismissed()
        .subscribe(result => this.logger.debug('Bottom sheet closed', result));
  }

  protected async openPolicy(policy: string) {
    if (this.sidePanelVisible()) {
      this.sidePanelVisible.set(false);
      await new Promise(resolve => setTimeout(resolve, 200));
    }
    const offcanvas = this.offcanvas()?.nativeElement;
    if (offcanvas) offcanvas.scrollTop = 0;
    const baseUrl = (url: string) => {
      try {
        const urlObj = new URL(url);
        return `${urlObj.protocol}/${urlObj.host}`;
      } catch (e) {
        this.logger.error('Invalid URL:', url);
        return null;
      }
    }
    const url = baseUrl(this.environment.serverUrl)
    if (url) {
      this.loading.set(true);
      this.sidePanelVisible.set(true);
      this.httpClient
          .get(`/media/${this.i18nService.getLanguage()}/policy/${policy}`, {
            responseType: 'text' as 'text',
            params: { embed: 'true' }
          })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (content) => {
              this.content.set(content || '');
              this.loading.set(false);
              this.sidePanelVisible.set(true);
            },
            error: (error) => {
              this.logger.error(`Failed to load ${policy}`, error);
              this.content.set('<p>Error loading content</p>');
              this.loading.set(false);
              this.sidePanelVisible.set(true);
            }
          });
    }
  }
  
  protected sidePanelVisibleChange(visible: boolean) {
    this.sidePanelVisible.set(visible);
  }

  protected onTapFooter(event: Event) {
    const target = event.target;
    if (target instanceof Element) {
      const link = target.closest('a') as HTMLAnchorElement | null;
      const modifierPressed = (event as MouseEvent).ctrlKey || (event as MouseEvent).metaKey; // desktop user can still use Cmd/Ctrl-click
      if (link && !modifierPressed) {
        if (this.platform.is('hybrid')) {
          event.preventDefault();
          const url = link.href;
          const browser = this.inAppBrowser.create(url, '_system');
          browser.on('exit')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
            browser.close();
          });
        } else {
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
          // do not interfere with default browser behavior
          // window.open() can trigger popup blocker
          // window.open(url, '_blank', 'noopener,noreferrer');
        }
      }
    }
  }
}